/**
 * ConsultingPage - Agent-led Proposal Builder
 * 6-step wizard: Discovery → Scope → Acceptance & KPIs → Planning → Estimation → Proposal
 * 
 * Feature flag: features.consulting.mode
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Briefcase, Search, Layout, CheckCircle, Calculator, FileText, Calendar,
  ChevronRight, ChevronLeft, Sparkles, Loader2, Save, Download,
  AlertTriangle, Check, X, Plus, Trash2, Edit2, RefreshCw,
  Users, Clock, DollarSign, Target, ArrowRight, BookOpen, Shield,
  Link2, BarChart3, FolderKanban, ArrowRightCircle, GitBranch, 
  Flag, UserCheck, CalendarClock, Milestone, Layers, Upload, File,
  RotateCcw
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import AcceptanceKPICard from '../components/AcceptanceKPICard';
import ProactiveSuggestionsBanner from '../components/ProactiveSuggestionsBanner';
import TierGate from '../components/TierGate';
import ProposalTemplatesSelector from '../components/ProposalTemplatesSelector';
import { toast } from 'sonner';

// Wizard steps - NOW 7 STEPS with Requirement Intake as first step
const STEPS = [
  { id: 'intake', label: 'Requirements', icon: Upload, description: 'Upload RFP/Spec' },
  { id: 'discovery', label: 'Discovery', icon: Search, description: 'Clarify client needs' },
  { id: 'scope', label: 'Scope', icon: Layout, description: 'Define scope tree' },
  { id: 'acceptance', label: 'Acceptance & KPIs', icon: CheckCircle, description: 'Success criteria' },
  { id: 'planning', label: 'Planning', icon: Calendar, description: 'Delivery plan' },
  { id: 'estimation', label: 'Estimation', icon: Calculator, description: 'Hours & timeline' },
  { id: 'proposal', label: 'Proposal', icon: FileText, description: 'Generate & export' },
];

// Role weights for estimation from planning
const ROLE_WEIGHTS = {
  'Developer': 1.0,
  'QA Engineer': 0.6,
  'Business Analyst': 0.4,
  'Product Owner': 0.4,
  'Project Manager': 0.3,
  'Architect': 0.4,
  'UX Designer': 0.3,
  'DevOps': 0.25,
  'Tech Lead': 0.5,
};

const AVAILABLE_ROLES = Object.keys(ROLE_WEIGHTS);

/**
 * Step Progress Indicator
 */
const StepProgress = ({ currentStep, steps, onStepClick }) => {
  const currentIdx = steps.findIndex(s => s.id === currentStep);
  
  return (
    <div className="flex items-center justify-center gap-1 p-4 bg-white border-b border-gray-200">
      {steps.map((step, idx) => {
        const isActive = step.id === currentStep;
        const isComplete = idx < currentIdx;
        const StepIcon = step.icon;
        
        return (
          <React.Fragment key={step.id}>
            <button
              onClick={() => isComplete && onStepClick?.(step.id)}
              disabled={!isComplete}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                isActive 
                  ? 'bg-purple-100 text-purple-700' 
                  : isComplete 
                    ? 'bg-green-50 text-green-700 hover:bg-green-100 cursor-pointer' 
                    : 'bg-gray-50 text-gray-400'
              }`}
              data-testid={`step-${step.id}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isActive 
                  ? 'bg-purple-600 text-white' 
                  : isComplete 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-200 text-gray-400'
              }`}>
                {isComplete ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium">{step.label}</div>
                <div className="text-[10px] opacity-70">{step.description}</div>
              </div>
            </button>
            {idx < steps.length - 1 && (
              <ChevronRight className={`w-4 h-4 ${idx < currentIdx ? 'text-green-500' : 'text-gray-300'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Requirement Intake Step Component - Upload RFP/Spec documents
 * AI extracts requirements and auto-populates discovery/scope/KPIs
 */
const IntakeStep = ({ data, onChange, onFileProcessed, tenantId, loading }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(data.extractedRequirements || null);
  const [file, setFile] = useState(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFile(droppedFile);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) handleFile(selectedFile);
  };

  const handleFile = async (selectedFile) => {
    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(pdf|docx|txt)$/i)) {
      toast.error('Please upload a PDF, Word (.docx), or Text file');
      return;
    }

    setFile(selectedFile);
    setUploading(true);

    try {
      // Create FormData and upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('tenant_id', tenantId);
      formData.append('extraction_mode', 'full');

      const response = await api.post('/scoping/upload-spec', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minute timeout for AI processing
      });

      const extracted = response.data;
      setExtractedData(extracted);
      
      // Pass extracted data up to parent
      onChange({
        ...data,
        extractedRequirements: extracted,
        sourceFile: selectedFile.name,
        uploadedAt: new Date().toISOString()
      });

      if (onFileProcessed) {
        onFileProcessed(extracted);
      }

      toast.success(`Extracted ${extracted.items?.length || 0} requirements from ${selectedFile.name}`);
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to process file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSkip = () => {
    onChange({ ...data, skipped: true });
    onFileProcessed?.({ skipped: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">Upload Requirements / RFP Document</h2>
        <p className="text-gray-600 mt-1">
          Upload client specs, RFPs, or requirement documents for AI-assisted extraction
        </p>
      </div>

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          isDragging 
            ? 'border-purple-500 bg-purple-50' 
            : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/50'
        }`}
        data-testid="intake-dropzone"
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
            <p className="text-gray-600">Processing document with AI...</p>
            <p className="text-sm text-gray-400">This may take up to 60 seconds</p>
          </div>
        ) : extractedData ? (
          <div className="flex flex-col items-center gap-3">
            <CheckCircle className="w-12 h-12 text-green-600" />
            <p className="text-green-700 font-medium">Document processed successfully!</p>
            <p className="text-gray-600">
              Extracted {extractedData.items?.length || 0} items from {file?.name || data.sourceFile}
            </p>
            <button
              onClick={() => {
                setExtractedData(null);
                setFile(null);
                onChange({ ...data, extractedRequirements: null, sourceFile: null });
              }}
              className="text-sm text-purple-600 hover:underline"
            >
              Upload a different file
            </button>
          </div>
        ) : (
          <>
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">Drag and drop your file here</p>
            <p className="text-gray-500 text-sm mt-1">or</p>
            <label className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer transition-colors">
              <File className="w-4 h-4" />
              Browse Files
              <input
                type="file"
                accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="intake-file-input"
              />
            </label>
            <p className="text-xs text-gray-400 mt-3">Supported: PDF, Word (.docx), Text (.txt)</p>
          </>
        )}
      </div>

      {/* Extracted Items Preview */}
      {extractedData && extractedData.items?.length > 0 && (
        <div className="border rounded-xl p-4 bg-gray-50">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            AI-Extracted Requirements Preview
          </h3>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {extractedData.items.slice(0, 10).map((item, idx) => (
              <div key={idx} className="p-2 bg-white rounded-lg border border-gray-200 text-sm">
                <span className="text-purple-600 font-medium">[{item.type}]</span>{' '}
                <span className="text-gray-700">{item.title || item.name}</span>
              </div>
            ))}
            {extractedData.items.length > 10 && (
              <p className="text-sm text-gray-500 text-center">
                + {extractedData.items.length - 10} more items
              </p>
            )}
          </div>
        </div>
      )}

      {/* Skip Option */}
      <div className="text-center pt-4 border-t">
        <button
          onClick={handleSkip}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
          data-testid="intake-skip-btn"
        >
          Skip this step and enter requirements manually
        </button>
      </div>
    </div>
  );
};

/**
 * Discovery Step Component
 */
const DiscoveryStep = ({ data, onChange, onGenerate, loading }) => {
  const [showQA, setShowQA] = useState(false);
  const [showTemplates, setShowTemplates] = useState(!data.clientContext && !data.problemStatement);
  
  // Templates for empty state
  const templates = [
    { name: 'Proposal Intake Form', format: 'DOCX', path: '/downloads/scopeops/templates/proposal-intake-form.docx', cta: 'consulting-template-intake-docx' },
    { name: 'Discovery Interview Script', format: 'PDF', path: '/downloads/scopeops/templates/discovery-interview-script.pdf', cta: 'consulting-template-discovery-pdf' },
    { name: 'Requirements Sheet', format: 'XLSX', path: '/downloads/scopeops/templates/requirements-acceptance-starter.xlsx', cta: 'consulting-template-reqs-xlsx' },
  ];
  
  const handleTemplateDownload = (template) => {
    // Track events
    if (window.trackEvent) {
      window.trackEvent('cta_click', { cta: template.cta });
      window.trackEvent('template_download', {
        templateName: template.name,
        page: 'consulting-discovery',
        timestamp: new Date().toISOString()
      });
    }
    
    const link = document.createElement('a');
    link.href = template.path;
    link.download = template.path.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="space-y-6">
      {/* Templates Helper - Empty State */}
      {showTemplates && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="discovery-templates-panel">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">Don't have inputs yet?</span>
            </div>
            <button 
              onClick={() => setShowTemplates(false)}
              className="text-blue-400 hover:text-blue-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-blue-700 mb-3">
            Start with our templates to structure your discovery sessions and capture requirements.
          </p>
          <div className="flex flex-wrap gap-2">
            {templates.map((t, idx) => (
              <button
                key={idx}
                onClick={() => handleTemplateDownload(t)}
                data-cta={t.cta}
                data-testid={`template-${t.format.toLowerCase()}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Download className="w-3 h-3" />
                {t.name} ({t.format})
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Client Context */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Client Context
        </label>
        <textarea
          value={data.clientContext || ''}
          onChange={(e) => onChange({ ...data, clientContext: e.target.value })}
          placeholder="Describe the client's business, industry, and current situation..."
          className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          rows={3}
        />
      </div>
      
      {/* Problem Statement */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Problem Statement
        </label>
        <textarea
          value={data.problemStatement || ''}
          onChange={(e) => onChange({ ...data, problemStatement: e.target.value })}
          placeholder="What problem needs to be solved? What does the client want to achieve?"
          className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          rows={3}
        />
      </div>
      
      {/* Industry & Constraints */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industry (optional)
          </label>
          <input
            type="text"
            value={data.industry || ''}
            onChange={(e) => onChange({ ...data, industry: e.target.value })}
            placeholder="e.g., Retail, Healthcare, Finance"
            className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Constraints (optional)
          </label>
          <input
            type="text"
            value={data.constraints || ''}
            onChange={(e) => onChange({ ...data, constraints: e.target.value })}
            placeholder="e.g., Must integrate with existing ERP"
            className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>
      
      {/* Generate Button */}
      <div className="flex justify-center">
        <button
          onClick={onGenerate}
          disabled={loading || !data.clientContext || !data.problemStatement}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
          data-testid="generate-discovery-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Sparkles className="w-5 h-5" />
          )}
          {loading ? 'Analyzing...' : 'Run AI Discovery'}
        </button>
      </div>
      
      {/* AI Results */}
      {data.refinedSummary && (
        <div className="space-y-4 pt-4 border-t border-gray-200">
          {/* Refined Summary */}
          <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg">
            <h4 className="text-sm font-medium text-purple-800 mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Refined Summary
            </h4>
            <p className="text-sm text-gray-700">{data.refinedSummary}</p>
          </div>
          
          {/* Clarifying Questions */}
          {data.clarifyingQuestions?.length > 0 && (
            <div>
              <button
                onClick={() => setShowQA(!showQA)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2"
              >
                <ChevronRight className={`w-4 h-4 transition-transform ${showQA ? 'rotate-90' : ''}`} />
                Clarifying Questions ({data.clarifyingQuestions.length})
              </button>
              {showQA && (
                <div className="space-y-2 pl-6">
                  {data.clarifyingQuestions.map((q, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                      <strong>Q{idx + 1}:</strong> {q}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* NFR/Ops Suggestions */}
          {data.suggestedNfrOps?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Suggested NFR/Ops Items ({data.suggestedNfrOps.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.suggestedNfrOps.map((nfr, idx) => (
                  <div key={idx} className="p-2 bg-blue-50 border border-blue-100 rounded-lg">
                    <span className="text-xs font-medium text-blue-600">{nfr.category}</span>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">{nfr.item}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Initial Risks */}
          {data.suggestedRisks?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Initial Risks ({data.suggestedRisks.length})
              </h4>
              <div className="space-y-2">
                {data.suggestedRisks.slice(0, 3).map((risk, idx) => (
                  <div key={idx} className="p-2 bg-orange-50 border border-orange-100 rounded-lg flex items-start gap-2">
                    <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                      risk.severity === 'high' ? 'text-red-500' : 
                      risk.severity === 'medium' ? 'text-orange-500' : 'text-yellow-500'
                    }`} />
                    <div>
                      <p className="text-xs text-gray-700">{risk.risk}</p>
                      <p className="text-[10px] text-gray-500 mt-1">Mitigation: {risk.mitigation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Scope Step Component
 */
const ScopeStep = ({ data, onChange, onGenerate, loading, discoveryData }) => {
  const [editingEpic, setEditingEpic] = useState(null);
  const [newFeatureName, setNewFeatureName] = useState('');
  
  const addEpic = () => {
    const newEpic = {
      id: `epic_${Date.now()}`,
      name: 'New Epic',
      description: '',
      features: []
    };
    onChange({
      ...data,
      scopeTree: {
        ...data.scopeTree,
        epics: [...(data.scopeTree?.epics || []), newEpic]
      }
    });
  };
  
  const removeEpic = (epicId) => {
    onChange({
      ...data,
      scopeTree: {
        ...data.scopeTree,
        epics: data.scopeTree.epics.filter(e => e.id !== epicId)
      }
    });
  };
  
  const updateEpic = (epicId, updates) => {
    onChange({
      ...data,
      scopeTree: {
        ...data.scopeTree,
        epics: data.scopeTree.epics.map(e => 
          e.id === epicId ? { ...e, ...updates } : e
        )
      }
    });
  };
  
  const addFeature = (epicId) => {
    if (!newFeatureName.trim()) return;
    
    const newFeature = {
      id: `feature_${Date.now()}`,
      name: newFeatureName.trim(),
      description: '',
      effort: 'M'
    };
    
    updateEpic(epicId, {
      features: [...(data.scopeTree.epics.find(e => e.id === epicId)?.features || []), newFeature]
    });
    setNewFeatureName('');
  };
  
  const removeFeature = (epicId, featureId) => {
    const epic = data.scopeTree.epics.find(e => e.id === epicId);
    if (epic) {
      updateEpic(epicId, {
        features: epic.features.filter(f => f.id !== featureId)
      });
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Generate from discovery */}
      {!data.scopeTree?.epics?.length && (
        <div className="text-center py-8">
          <Layout className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Generate Scope Tree</h3>
          <p className="text-sm text-gray-500 mb-4">
            Use AI to generate an initial scope tree from the discovery insights
          </p>
          <button
            onClick={onGenerate}
            disabled={loading || !discoveryData?.refinedSummary}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
            data-testid="generate-scope-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            {loading ? 'Generating...' : 'Generate Scope'}
          </button>
        </div>
      )}
      
      {/* Scope Tree Display */}
      {data.scopeTree?.epics?.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">
              Scope Tree ({data.scopeTree.epics.length} Epics)
            </h3>
            <div className="flex gap-2">
              <button
                onClick={onGenerate}
                disabled={loading}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-purple-600 hover:bg-purple-50 rounded-lg"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                Regenerate
              </button>
              <button
                onClick={addEpic}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Plus className="w-3 h-3" />
                Add Epic
              </button>
            </div>
          </div>
          
          {/* Epics List */}
          <div className="space-y-3">
            {data.scopeTree.epics.map((epic) => (
              <div 
                key={epic.id} 
                className="p-4 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    {editingEpic === epic.id ? (
                      <input
                        type="text"
                        value={epic.name}
                        onChange={(e) => updateEpic(epic.id, { name: e.target.value })}
                        onBlur={() => setEditingEpic(null)}
                        autoFocus
                        className="w-full px-2 py-1 text-sm font-medium border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
                      />
                    ) : (
                      <h4 
                        className="font-medium text-gray-900 cursor-pointer hover:text-purple-600"
                        onClick={() => setEditingEpic(epic.id)}
                      >
                        {epic.name}
                      </h4>
                    )}
                    <p className="text-xs text-gray-500 mt-1">{epic.description}</p>
                  </div>
                  <button
                    onClick={() => removeEpic(epic.id)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Features */}
                <div className="space-y-2 pl-4 border-l-2 border-purple-100">
                  {epic.features?.map((feature) => (
                    <div 
                      key={feature.id}
                      className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">{feature.name}</span>
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                          feature.effort === 'S' ? 'bg-green-100 text-green-700' :
                          feature.effort === 'M' ? 'bg-yellow-100 text-yellow-700' :
                          feature.effort === 'L' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {feature.effort}
                        </span>
                      </div>
                      <button
                        onClick={() => removeFeature(epic.id, feature.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Add Feature */}
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="text"
                      value={newFeatureName}
                      onChange={(e) => setNewFeatureName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addFeature(epic.id)}
                      placeholder="Add feature..."
                      className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={() => addFeature(epic.id)}
                      disabled={!newFeatureName.trim()}
                      className="p-1 text-purple-600 hover:bg-purple-50 rounded disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Assumptions & Dependencies */}
          {(data.assumptions?.length > 0 || data.dependencies?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              {data.assumptions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Assumptions</h4>
                  <ul className="space-y-1">
                    {data.assumptions.map((a, idx) => (
                      <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                        <span className="text-purple-500">•</span>
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.dependencies?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Dependencies</h4>
                  <ul className="space-y-1">
                    {data.dependencies.map((d, idx) => (
                      <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                        <span className="text-orange-500">•</span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Planning Step Component
 * Full delivery planning: Phases, Milestones, Roles, Governance, Risks, Dependencies
 */
const PlanningStep = ({ data, onChange, scopeData, discoveryData }) => {
  const [activeSection, setActiveSection] = useState('phases');
  const [editingItem, setEditingItem] = useState(null);
  
  // Initialize plan structure if not present
  useEffect(() => {
    if (!data.phases?.length) {
      // Auto-generate initial phases from scope
      const defaultPhases = [
        { id: `phase_${Date.now()}_1`, name: 'Discovery & Planning', durationWeeks: 2, startWeek: 1, deliverables: ['Requirements document', 'Project plan'] },
        { id: `phase_${Date.now()}_2`, name: 'Development', durationWeeks: 6, startWeek: 3, deliverables: ['Core features', 'Integration points'] },
        { id: `phase_${Date.now()}_3`, name: 'Testing & QA', durationWeeks: 2, startWeek: 9, deliverables: ['Test reports', 'Bug fixes'] },
        { id: `phase_${Date.now()}_4`, name: 'UAT & Deployment', durationWeeks: 2, startWeek: 11, deliverables: ['User acceptance', 'Go-live'] },
      ];
      onChange({ ...data, phases: defaultPhases });
    }
  }, []);
  
  // Calculate total duration
  const totalWeeks = data.phases?.reduce((sum, p) => sum + (p.durationWeeks || 0), 0) || 0;
  
  // Sections configuration
  const sections = [
    { id: 'phases', label: 'Phases & Timeline', icon: Layers, count: data.phases?.length || 0 },
    { id: 'milestones', label: 'Milestones', icon: Flag, count: data.milestones?.length || 0 },
    { id: 'roles', label: 'Roles & RACI', icon: UserCheck, count: data.roles?.length || 0 },
    { id: 'governance', label: 'Governance', icon: CalendarClock, count: data.governance?.cadence?.length || 0 },
    { id: 'risks', label: 'Risks', icon: AlertTriangle, count: data.risks?.length || 0 },
    { id: 'dependencies', label: 'Dependencies', icon: GitBranch, count: data.dependencies?.length || 0 },
  ];
  
  // Phase handlers
  const addPhase = () => {
    const maxStartWeek = data.phases?.reduce((max, p) => Math.max(max, (p.startWeek || 0) + (p.durationWeeks || 0)), 0) || 0;
    const newPhase = {
      id: `phase_${Date.now()}`,
      name: 'New Phase',
      durationWeeks: 2,
      startWeek: maxStartWeek + 1,
      deliverables: []
    };
    onChange({ ...data, phases: [...(data.phases || []), newPhase] });
  };
  
  const updatePhase = (phaseId, updates) => {
    onChange({
      ...data,
      phases: data.phases.map(p => p.id === phaseId ? { ...p, ...updates } : p)
    });
  };
  
  const removePhase = (phaseId) => {
    onChange({ ...data, phases: data.phases.filter(p => p.id !== phaseId) });
  };
  
  // Milestone handlers
  const addMilestone = () => {
    const newMilestone = {
      id: `ms_${Date.now()}`,
      name: 'New Milestone',
      phaseId: data.phases?.[0]?.id || null,
      targetWeek: 1,
      criteria: [],
      status: 'pending'
    };
    onChange({ ...data, milestones: [...(data.milestones || []), newMilestone] });
  };
  
  const updateMilestone = (msId, updates) => {
    onChange({
      ...data,
      milestones: data.milestones.map(m => m.id === msId ? { ...m, ...updates } : m)
    });
  };
  
  const removeMilestone = (msId) => {
    onChange({ ...data, milestones: data.milestones.filter(m => m.id !== msId) });
  };
  
  // Role handlers
  const addRole = () => {
    const newRole = {
      id: `role_${Date.now()}`,
      name: 'New Role',
      type: 'Developer',
      allocation: 100,
      responsibilities: [],
      raci: { R: [], A: [], C: [], I: [] }
    };
    onChange({ ...data, roles: [...(data.roles || []), newRole] });
  };
  
  const updateRole = (roleId, updates) => {
    onChange({
      ...data,
      roles: data.roles.map(r => r.id === roleId ? { ...r, ...updates } : r)
    });
  };
  
  const removeRole = (roleId) => {
    onChange({ ...data, roles: data.roles.filter(r => r.id !== roleId) });
  };
  
  // Governance handlers
  const addGovernanceCadence = () => {
    const newCadence = {
      id: `gov_${Date.now()}`,
      name: 'New Checkpoint',
      type: 'weekly',
      attendees: [],
      agenda: []
    };
    const governance = data.governance || { cadence: [], driftDetection: { enabled: false, thresholds: {} } };
    onChange({
      ...data,
      governance: {
        ...governance,
        cadence: [...(governance.cadence || []), newCadence]
      }
    });
  };
  
  const updateGovernance = (updates) => {
    onChange({
      ...data,
      governance: { ...(data.governance || {}), ...updates }
    });
  };
  
  // Risk handlers
  const addRisk = () => {
    const newRisk = {
      id: `risk_${Date.now()}`,
      title: 'New Risk',
      description: '',
      likelihood: 'medium',
      impact: 'medium',
      mitigation: '',
      owner: '',
      status: 'open'
    };
    onChange({ ...data, risks: [...(data.risks || []), newRisk] });
  };
  
  const updateRisk = (riskId, updates) => {
    onChange({
      ...data,
      risks: data.risks.map(r => r.id === riskId ? { ...r, ...updates } : r)
    });
  };
  
  const removeRisk = (riskId) => {
    onChange({ ...data, risks: data.risks.filter(r => r.id !== riskId) });
  };
  
  // Dependency handlers
  const addDependency = () => {
    const newDep = {
      id: `dep_${Date.now()}`,
      title: 'New Dependency',
      type: 'external',
      status: 'pending',
      dueDate: null,
      owner: '',
      notes: ''
    };
    onChange({ ...data, dependencies: [...(data.dependencies || []), newDep] });
  };
  
  const updateDependency = (depId, updates) => {
    onChange({
      ...data,
      dependencies: data.dependencies.map(d => d.id === depId ? { ...d, ...updates } : d)
    });
  };
  
  const removeDependency = (depId) => {
    onChange({ ...data, dependencies: data.dependencies.filter(d => d.id !== depId) });
  };
  
  // Auto-populate risks from discovery
  const importDiscoveryRisks = () => {
    if (!discoveryData?.suggestedRisks?.length) return;
    
    const existingRiskTitles = new Set(data.risks?.map(r => r.title.toLowerCase()) || []);
    const newRisks = discoveryData.suggestedRisks
      .filter(r => !existingRiskTitles.has(r.risk.toLowerCase()))
      .map((r, idx) => ({
        id: `risk_import_${Date.now()}_${idx}`,
        title: r.risk,
        description: '',
        likelihood: r.severity === 'high' ? 'high' : r.severity === 'low' ? 'low' : 'medium',
        impact: r.severity === 'high' ? 'high' : r.severity === 'low' ? 'low' : 'medium',
        mitigation: r.mitigation || '',
        owner: '',
        status: 'open'
      }));
    
    if (newRisks.length > 0) {
      onChange({ ...data, risks: [...(data.risks || []), ...newRisks] });
      toast.success(`Imported ${newRisks.length} risks from discovery`);
    } else {
      toast.info('No new risks to import');
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Summary Bar */}
      <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Calendar className="w-6 h-6 text-purple-600" />
            <div>
              <h3 className="font-medium text-gray-900">Delivery Plan</h3>
              <p className="text-sm text-gray-600">
                {data.phases?.length || 0} phases • {totalWeeks} weeks total duration
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
              <Flag className="w-3 h-3 text-green-600" />
              {data.milestones?.length || 0} milestones
            </span>
            <span className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
              <Users className="w-3 h-3 text-blue-600" />
              {data.roles?.length || 0} roles
            </span>
            <span className="flex items-center gap-1 px-2 py-1 bg-white rounded border">
              <AlertTriangle className="w-3 h-3 text-orange-500" />
              {data.risks?.length || 0} risks
            </span>
          </div>
        </div>
      </div>
      
      {/* Section Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg overflow-x-auto">
        {sections.map((section) => {
          const SectionIcon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              data-testid={`planning-section-${section.id}`}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${
                activeSection === section.id 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <SectionIcon className="w-4 h-4" />
              {section.label}
              {section.count > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] bg-purple-100 text-purple-700 rounded-full">
                  {section.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
      
      {/* Section Content */}
      <div className="min-h-[400px]">
        {/* Phases & Timeline */}
        {activeSection === 'phases' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">Project Phases</h4>
              <button
                onClick={addPhase}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="add-phase-btn"
              >
                <Plus className="w-3 h-3" />
                Add Phase
              </button>
            </div>
            
            {/* Timeline Visual */}
            <div className="p-4 bg-gray-50 rounded-lg overflow-x-auto">
              <div className="flex gap-2 min-w-max">
                {data.phases?.map((phase, idx) => (
                  <div
                    key={phase.id}
                    className="relative bg-white border border-gray-200 rounded-lg p-3"
                    style={{ width: `${Math.max(phase.durationWeeks * 50, 120)}px` }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-xs text-purple-600 font-medium">Phase {idx + 1}</span>
                      <button
                        onClick={() => removePhase(phase.id)}
                        className="p-0.5 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={phase.name}
                      onChange={(e) => updatePhase(phase.id, { name: e.target.value })}
                      className="w-full text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:ring-0"
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="number"
                        value={phase.durationWeeks}
                        onChange={(e) => updatePhase(phase.id, { durationWeeks: parseInt(e.target.value) || 1 })}
                        className="w-12 px-1 py-0.5 text-xs text-center border border-gray-200 rounded"
                        min={1}
                      />
                      <span className="text-xs text-gray-500">weeks</span>
                    </div>
                    {/* Progress bar based on position */}
                    <div className="mt-2 h-1 bg-purple-200 rounded-full">
                      <div 
                        className="h-1 bg-purple-600 rounded-full" 
                        style={{ width: `${(idx + 1) / (data.phases?.length || 1) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Phase Details List */}
            <div className="space-y-2">
              {data.phases?.map((phase, idx) => (
                <div key={phase.id} className="p-3 bg-white border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 flex items-center justify-center bg-purple-100 text-purple-700 text-xs font-medium rounded">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-gray-900">{phase.name}</span>
                    </div>
                    <span className="text-sm text-gray-500">
                      Week {phase.startWeek || idx * 2 + 1} - {(phase.startWeek || idx * 2 + 1) + phase.durationWeeks - 1}
                    </span>
                  </div>
                  {/* Deliverables */}
                  <div className="mt-2 pl-9">
                    <p className="text-xs text-gray-500 mb-1">Deliverables:</p>
                    <div className="flex flex-wrap gap-1">
                      {phase.deliverables?.map((d, didx) => (
                        <span key={didx} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                          {d}
                        </span>
                      ))}
                      <input
                        type="text"
                        placeholder="+ Add deliverable"
                        className="px-2 py-0.5 text-xs border border-dashed border-gray-300 rounded focus:border-purple-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            updatePhase(phase.id, {
                              deliverables: [...(phase.deliverables || []), e.target.value.trim()]
                            });
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Milestones */}
        {activeSection === 'milestones' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">Key Milestones</h4>
              <button
                onClick={addMilestone}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="add-milestone-btn"
              >
                <Plus className="w-3 h-3" />
                Add Milestone
              </button>
            </div>
            
            <div className="space-y-2">
              {(data.milestones || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Flag className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No milestones defined yet</p>
                  <p className="text-xs text-gray-400">Click "Add Milestone" to create checkpoints</p>
                </div>
              ) : (
                data.milestones.map((ms) => (
                  <div key={ms.id} className="p-3 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <Flag className="w-4 h-4 text-green-600" />
                        <input
                          type="text"
                          value={ms.name}
                          onChange={(e) => updateMilestone(ms.id, { name: e.target.value })}
                          className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:ring-0"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={ms.phaseId || ''}
                          onChange={(e) => updateMilestone(ms.id, { phaseId: e.target.value })}
                          className="text-xs border border-gray-200 rounded px-2 py-1"
                        >
                          <option value="">Select phase</option>
                          {data.phases?.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          value={ms.targetWeek || 1}
                          onChange={(e) => updateMilestone(ms.id, { targetWeek: parseInt(e.target.value) || 1 })}
                          className="w-16 px-2 py-1 text-xs border border-gray-200 rounded text-center"
                          placeholder="Week"
                          min={1}
                        />
                        <button
                          onClick={() => removeMilestone(ms.id)}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    {/* Success Criteria */}
                    <div className="mt-2 pl-7">
                      <p className="text-xs text-gray-500 mb-1">Success Criteria:</p>
                      <div className="flex flex-wrap gap-1">
                        {ms.criteria?.map((c, cidx) => (
                          <span key={cidx} className="px-2 py-0.5 text-xs bg-green-50 text-green-700 rounded">
                            {c}
                          </span>
                        ))}
                        <input
                          type="text"
                          placeholder="+ Add criterion"
                          className="px-2 py-0.5 text-xs border border-dashed border-gray-300 rounded focus:border-green-500"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              updateMilestone(ms.id, {
                                criteria: [...(ms.criteria || []), e.target.value.trim()]
                              });
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Roles & RACI */}
        {activeSection === 'roles' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">Team Roles & Responsibilities</h4>
              <button
                onClick={addRole}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="add-role-btn"
              >
                <Plus className="w-3 h-3" />
                Add Role
              </button>
            </div>
            
            <div className="space-y-2">
              {(data.roles || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No roles defined yet</p>
                  <p className="text-xs text-gray-400">Add roles to define team structure</p>
                </div>
              ) : (
                data.roles.map((role) => (
                  <div key={role.id} className="p-3 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <UserCheck className="w-4 h-4 text-blue-600" />
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <input
                            type="text"
                            value={role.name}
                            onChange={(e) => updateRole(role.id, { name: e.target.value })}
                            placeholder="Role name"
                            className="text-sm font-medium text-gray-900 border border-gray-200 rounded px-2 py-1"
                          />
                          <select
                            value={role.type}
                            onChange={(e) => updateRole(role.id, { type: e.target.value })}
                            className="text-xs border border-gray-200 rounded px-2 py-1"
                          >
                            {AVAILABLE_ROLES.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={role.allocation}
                              onChange={(e) => updateRole(role.id, { allocation: parseInt(e.target.value) || 100 })}
                              className="w-16 px-2 py-1 text-xs border border-gray-200 rounded text-center"
                              min={0}
                              max={100}
                            />
                            <span className="text-xs text-gray-500">% allocation</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeRole(role.id)}
                        className="p-1 text-gray-400 hover:text-red-500 ml-2"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    {/* Responsibilities */}
                    <div className="mt-2 pl-7">
                      <p className="text-xs text-gray-500 mb-1">Responsibilities:</p>
                      <div className="flex flex-wrap gap-1">
                        {role.responsibilities?.map((r, ridx) => (
                          <span key={ridx} className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                            {r}
                          </span>
                        ))}
                        <input
                          type="text"
                          placeholder="+ Add responsibility"
                          className="px-2 py-0.5 text-xs border border-dashed border-gray-300 rounded focus:border-blue-500"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              updateRole(role.id, {
                                responsibilities: [...(role.responsibilities || []), e.target.value.trim()]
                              });
                              e.target.value = '';
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Governance */}
        {activeSection === 'governance' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">Governance Cadence</h4>
              <button
                onClick={addGovernanceCadence}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="add-governance-btn"
              >
                <Plus className="w-3 h-3" />
                Add Checkpoint
              </button>
            </div>
            
            {/* Drift Detection Toggle */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-medium text-gray-700">Drift Detection</h5>
                  <p className="text-xs text-gray-500">Auto-flag deviations from plan</p>
                </div>
                <button
                  onClick={() => updateGovernance({
                    driftDetection: {
                      ...(data.governance?.driftDetection || {}),
                      enabled: !(data.governance?.driftDetection?.enabled)
                    }
                  })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    data.governance?.driftDetection?.enabled ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                    data.governance?.driftDetection?.enabled ? 'left-5' : 'left-0.5'
                  }`} />
                </button>
              </div>
              {data.governance?.driftDetection?.enabled && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Schedule variance threshold (%)</label>
                    <input
                      type="number"
                      value={data.governance?.driftDetection?.thresholds?.schedule || 10}
                      onChange={(e) => updateGovernance({
                        driftDetection: {
                          ...data.governance?.driftDetection,
                          thresholds: {
                            ...data.governance?.driftDetection?.thresholds,
                            schedule: parseInt(e.target.value) || 10
                          }
                        }
                      })}
                      className="w-full px-2 py-1 text-xs border border-gray-200 rounded mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Cost variance threshold (%)</label>
                    <input
                      type="number"
                      value={data.governance?.driftDetection?.thresholds?.cost || 15}
                      onChange={(e) => updateGovernance({
                        driftDetection: {
                          ...data.governance?.driftDetection,
                          thresholds: {
                            ...data.governance?.driftDetection?.thresholds,
                            cost: parseInt(e.target.value) || 15
                          }
                        }
                      })}
                      className="w-full px-2 py-1 text-xs border border-gray-200 rounded mt-1"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Cadence Items */}
            <div className="space-y-2">
              {(data.governance?.cadence || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CalendarClock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No governance checkpoints defined</p>
                  <p className="text-xs text-gray-400">Add regular check-ins and reviews</p>
                </div>
              ) : (
                data.governance.cadence.map((gov) => (
                  <div key={gov.id} className="p-3 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <CalendarClock className="w-4 h-4 text-purple-600" />
                        <input
                          type="text"
                          value={gov.name}
                          onChange={(e) => {
                            const cadence = data.governance.cadence.map(g =>
                              g.id === gov.id ? { ...g, name: e.target.value } : g
                            );
                            updateGovernance({ cadence });
                          }}
                          className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:ring-0"
                        />
                      </div>
                      <select
                        value={gov.type}
                        onChange={(e) => {
                          const cadence = data.governance.cadence.map(g =>
                            g.id === gov.id ? { ...g, type: e.target.value } : g
                          );
                          updateGovernance({ cadence });
                        }}
                        className="text-xs border border-gray-200 rounded px-2 py-1"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="milestone">Per Milestone</option>
                      </select>
                      <button
                        onClick={() => {
                          const cadence = data.governance.cadence.filter(g => g.id !== gov.id);
                          updateGovernance({ cadence });
                        }}
                        className="p-1 text-gray-400 hover:text-red-500 ml-2"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Risks */}
        {activeSection === 'risks' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">Project Risks</h4>
              <div className="flex gap-2">
                {discoveryData?.suggestedRisks?.length > 0 && (
                  <button
                    onClick={importDiscoveryRisks}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50"
                  >
                    <Download className="w-3 h-3" />
                    Import from Discovery
                  </button>
                )}
                <button
                  onClick={addRisk}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  data-testid="add-risk-btn"
                >
                  <Plus className="w-3 h-3" />
                  Add Risk
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              {(data.risks || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No risks identified yet</p>
                  <p className="text-xs text-gray-400">Add risks to track and mitigate</p>
                </div>
              ) : (
                data.risks.map((risk) => (
                  <div key={risk.id} className="p-3 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`w-4 h-4 mt-1 ${
                        risk.likelihood === 'high' || risk.impact === 'high' ? 'text-red-500' :
                        risk.likelihood === 'medium' || risk.impact === 'medium' ? 'text-orange-500' : 'text-yellow-500'
                      }`} />
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={risk.title}
                          onChange={(e) => updateRisk(risk.id, { title: e.target.value })}
                          className="w-full text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:ring-0"
                        />
                        <div className="grid grid-cols-4 gap-2">
                          <select
                            value={risk.likelihood}
                            onChange={(e) => updateRisk(risk.id, { likelihood: e.target.value })}
                            className="text-xs border border-gray-200 rounded px-2 py-1"
                          >
                            <option value="low">Low likelihood</option>
                            <option value="medium">Medium likelihood</option>
                            <option value="high">High likelihood</option>
                          </select>
                          <select
                            value={risk.impact}
                            onChange={(e) => updateRisk(risk.id, { impact: e.target.value })}
                            className="text-xs border border-gray-200 rounded px-2 py-1"
                          >
                            <option value="low">Low impact</option>
                            <option value="medium">Medium impact</option>
                            <option value="high">High impact</option>
                          </select>
                          <select
                            value={risk.status}
                            onChange={(e) => updateRisk(risk.id, { status: e.target.value })}
                            className="text-xs border border-gray-200 rounded px-2 py-1"
                          >
                            <option value="open">Open</option>
                            <option value="mitigating">Mitigating</option>
                            <option value="closed">Closed</option>
                          </select>
                          <input
                            type="text"
                            value={risk.owner || ''}
                            onChange={(e) => updateRisk(risk.id, { owner: e.target.value })}
                            placeholder="Owner"
                            className="text-xs border border-gray-200 rounded px-2 py-1"
                          />
                        </div>
                        <input
                          type="text"
                          value={risk.mitigation || ''}
                          onChange={(e) => updateRisk(risk.id, { mitigation: e.target.value })}
                          placeholder="Mitigation strategy..."
                          className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                        />
                      </div>
                      <button
                        onClick={() => removeRisk(risk.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Dependencies */}
        {activeSection === 'dependencies' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">Project Dependencies</h4>
              <button
                onClick={addDependency}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="add-dependency-btn"
              >
                <Plus className="w-3 h-3" />
                Add Dependency
              </button>
            </div>
            
            <div className="space-y-2">
              {(data.dependencies || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <GitBranch className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No dependencies defined</p>
                  <p className="text-xs text-gray-400">Track external and internal dependencies</p>
                </div>
              ) : (
                data.dependencies.map((dep) => (
                  <div key={dep.id} className="p-3 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <GitBranch className="w-4 h-4 mt-1 text-teal-600" />
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={dep.title}
                          onChange={(e) => updateDependency(dep.id, { title: e.target.value })}
                          className="w-full text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:ring-0"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <select
                            value={dep.type}
                            onChange={(e) => updateDependency(dep.id, { type: e.target.value })}
                            className="text-xs border border-gray-200 rounded px-2 py-1"
                          >
                            <option value="external">External</option>
                            <option value="internal">Internal</option>
                            <option value="technical">Technical</option>
                            <option value="resource">Resource</option>
                          </select>
                          <select
                            value={dep.status}
                            onChange={(e) => updateDependency(dep.id, { status: e.target.value })}
                            className="text-xs border border-gray-200 rounded px-2 py-1"
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="blocked">Blocked</option>
                          </select>
                          <input
                            type="text"
                            value={dep.owner || ''}
                            onChange={(e) => updateDependency(dep.id, { owner: e.target.value })}
                            placeholder="Owner"
                            className="text-xs border border-gray-200 rounded px-2 py-1"
                          />
                        </div>
                        <input
                          type="text"
                          value={dep.notes || ''}
                          onChange={(e) => updateDependency(dep.id, { notes: e.target.value })}
                          placeholder="Notes..."
                          className="w-full text-xs border border-gray-200 rounded px-2 py-1"
                        />
                      </div>
                      <button
                        onClick={() => removeDependency(dep.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Estimation Step Component
 */
const EstimationStep = ({ data, onChange, onGenerate, loading, scopeData, planData, rateCard, currency, onOpenRateCard }) => {
  const updateRole = (roleIndex, updates) => {
    const newRoles = [...(data.roles || [])];
    newRoles[roleIndex] = { ...newRoles[roleIndex], ...updates };
    onChange({ ...data, roles: newRoles });
  };
  
  const updatePhase = (phaseIndex, updates) => {
    const newTimeline = [...(data.timeline || [])];
    newTimeline[phaseIndex] = { ...newTimeline[phaseIndex], ...updates };
    onChange({ ...data, timeline: newTimeline });
  };
  
  // Check for missing rates
  const rolesWithMissingRates = data.roles?.filter(role => {
    const rateCardRole = rateCard?.find(r => r.role === role.role && r.active);
    return !rateCardRole;
  }) || [];
  
  const hasAllRates = rateCard?.length > 0 && rolesWithMissingRates.length === 0;
  const currencySymbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : currency === 'ZAR' ? 'R' : '$';
  
  return (
    <div className="space-y-6">
      {/* Generate Estimation */}
      {!data.roles?.length && (
        <div className="text-center py-8">
          <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Generate Estimation</h3>
          <p className="text-sm text-gray-500 mb-4">
            Use AI to estimate hours by role and create a project timeline
          </p>
          <button
            onClick={onGenerate}
            disabled={loading || !scopeData?.scopeTree?.epics?.length}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
            data-testid="generate-estimation-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            {loading ? 'Estimating...' : 'Generate Estimation'}
          </button>
        </div>
      )}
      
      {/* Estimation Results */}
      {data.roles?.length > 0 && (
        <div className="space-y-6">
          {/* Missing Rates Warning */}
          {rolesWithMissingRates.length > 0 && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  Missing rates for: {rolesWithMissingRates.map(r => r.role).join(', ')}
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Set rates to include costs in the proposal, or export hours-only.
                </p>
              </div>
              <button
                onClick={onOpenRateCard}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200"
              >
                <DollarSign className="w-3 h-3" />
                Set Rates
              </button>
            </div>
          )}
          
          {/* Role Hours Table */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-500" />
                Effort by Role
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={onOpenRateCard}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-green-600 hover:bg-green-50 rounded-lg"
                >
                  <DollarSign className="w-3 h-3" />
                  Rate Card
                </button>
                <button
                  onClick={onGenerate}
                  disabled={loading}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-purple-600 hover:bg-purple-50 rounded-lg"
                >
                  <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                  Regenerate
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Role</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Hours (Min)</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Hours (Max)</th>
                    {hasAllRates && (
                      <>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Rate</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Cost Range</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.roles.map((role, idx) => {
                    const rateCardRole = rateCard?.find(r => r.role === role.role && r.active);
                    const effectiveRate = rateCardRole 
                      ? rateCardRole.baseRate * (1 + (rateCardRole.markupPct || 0) / 100)
                      : null;
                    const costMin = effectiveRate ? role.hoursMin * effectiveRate : null;
                    const costMax = effectiveRate ? role.hoursMax * effectiveRate : null;
                    
                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{role.role}</span>
                            {!rateCardRole && rateCard?.length > 0 && (
                              <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded">
                                No rate
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <input
                            type="number"
                            value={role.hoursMin}
                            onChange={(e) => updateRole(idx, { hoursMin: parseFloat(e.target.value) || 0 })}
                            className="w-20 px-2 py-1 text-sm text-center border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          <input
                            type="number"
                            value={role.hoursMax}
                            onChange={(e) => updateRole(idx, { hoursMax: parseFloat(e.target.value) || 0 })}
                            className="w-20 px-2 py-1 text-sm text-center border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
                          />
                        </td>
                        {hasAllRates && (
                          <>
                            <td className="px-4 py-2 text-right text-sm text-gray-600">
                              {effectiveRate ? `${currencySymbol}${effectiveRate.toFixed(0)}/hr` : '—'}
                            </td>
                            <td className="px-4 py-2 text-right text-sm text-gray-600">
                              {costMin != null 
                                ? `${currencySymbol}${costMin.toLocaleString(undefined, {maximumFractionDigits: 0})} - ${currencySymbol}${costMax.toLocaleString(undefined, {maximumFractionDigits: 0})}`
                                : '—'
                              }
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 font-medium">
                  <tr>
                    <td className="px-4 py-2 text-sm text-gray-900">Total</td>
                    <td className="px-4 py-2 text-center text-sm text-gray-900">{data.totalHoursMin?.toLocaleString()}</td>
                    <td className="px-4 py-2 text-center text-sm text-gray-900">{data.totalHoursMax?.toLocaleString()}</td>
                    {hasAllRates && (
                      <>
                        <td className="px-4 py-2 text-right text-sm text-gray-500">
                          {data.blendedRate ? `${currencySymbol}${data.blendedRate.toFixed(0)}/hr avg` : '—'}
                        </td>
                        <td className="px-4 py-2 text-right text-sm text-gray-900">
                          {data.totalCostMin != null
                            ? `${currencySymbol}${data.totalCostMin.toLocaleString(undefined, {maximumFractionDigits: 0})} - ${currencySymbol}${data.totalCostMax.toLocaleString(undefined, {maximumFractionDigits: 0})}`
                            : '—'
                          }
                        </td>
                      </>
                    )}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          
          {/* Timeline */}
          {data.timeline?.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-purple-500" />
                Project Timeline
              </h3>
              
              <div className="space-y-2">
                {data.timeline.map((phase, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-4 p-3 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">{phase.phase}</div>
                      {phase.notes && (
                        <div className="text-xs text-gray-500">{phase.notes}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={phase.durationWeeks}
                        onChange={(e) => updatePhase(idx, { durationWeeks: parseFloat(e.target.value) || 0 })}
                        className="w-16 px-2 py-1 text-sm text-center border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
                        min={0}
                      />
                      <span className="text-xs text-gray-500">weeks</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-3 p-3 bg-purple-50 rounded-lg text-center">
                <span className="text-sm font-medium text-purple-700">
                  Total Duration: {data.timeline.reduce((sum, p) => sum + (p.durationWeeks || 0), 0)} weeks
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Variable Costs Editor Component
 */
const VariableCostsEditor = ({ costs = [], onChange, currency = 'USD' }) => {
  const [newCost, setNewCost] = useState({ category: 'hardware', description: '', amount: 0, is_recurring: false, frequency: 'one-time' });
  
  const categories = [
    { id: 'hardware', label: 'Hardware', icon: '🖥️' },
    { id: 'software', label: 'Software/Licenses', icon: '📦' },
    { id: 'travel', label: 'Travel & Expenses', icon: '✈️' },
    { id: 'storage', label: 'Cloud/Storage', icon: '☁️' },
    { id: 'training', label: 'Training', icon: '📚' },
    { id: 'other', label: 'Other', icon: '📋' },
  ];
  
  const frequencies = ['one-time', 'monthly', 'quarterly', 'annual'];
  
  const currencySymbols = { USD: '$', EUR: '€', GBP: '£', ZAR: 'R', AUD: 'A$', CAD: 'C$', INR: '₹' };
  const symbol = currencySymbols[currency] || '$';
  
  const addCost = () => {
    if (!newCost.description.trim() || newCost.amount <= 0) return;
    onChange([...costs, { ...newCost, id: `vc_${Date.now()}` }]);
    setNewCost({ category: 'hardware', description: '', amount: 0, is_recurring: false, frequency: 'one-time' });
  };
  
  const removeCost = (id) => {
    onChange(costs.filter(c => c.id !== id));
  };
  
  const totalOneTime = costs.filter(c => !c.is_recurring).reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalRecurring = costs.filter(c => c.is_recurring).reduce((sum, c) => sum + (c.amount || 0), 0);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-green-600" />
          Variable Costs (Non-Resource)
        </h4>
        {costs.length > 0 && (
          <div className="flex gap-3 text-xs">
            <span className="px-2 py-1 bg-gray-100 rounded">One-time: {symbol}{totalOneTime.toLocaleString()}</span>
            {totalRecurring > 0 && (
              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">Recurring: {symbol}{totalRecurring.toLocaleString()}/mo</span>
            )}
          </div>
        )}
      </div>
      
      {/* Existing Costs */}
      {costs.length > 0 && (
        <div className="space-y-2">
          {costs.map((cost) => {
            const cat = categories.find(c => c.id === cost.category);
            return (
              <div key={cost.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{cat?.icon || '📋'}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{cost.description}</p>
                    <p className="text-xs text-gray-500">{cat?.label || cost.category}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{symbol}{cost.amount?.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{cost.is_recurring ? cost.frequency : 'one-time'}</p>
                  </div>
                  <button onClick={() => removeCost(cost.id)} className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Add New Cost Form */}
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3">
            <label className="block text-xs text-gray-500 mb-1">Category</label>
            <select
              value={newCost.category}
              onChange={(e) => setNewCost({ ...newCost, category: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
              ))}
            </select>
          </div>
          <div className="col-span-4">
            <label className="block text-xs text-gray-500 mb-1">Description</label>
            <input
              type="text"
              value={newCost.description}
              onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
              placeholder="e.g., AWS hosting"
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Amount ({currency})</label>
            <input
              type="number"
              value={newCost.amount || ''}
              onChange={(e) => setNewCost({ ...newCost, amount: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
              min={0}
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Frequency</label>
            <select
              value={newCost.is_recurring ? newCost.frequency : 'one-time'}
              onChange={(e) => {
                const val = e.target.value;
                setNewCost({ ...newCost, is_recurring: val !== 'one-time', frequency: val });
              }}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-purple-500"
            >
              {frequencies.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div className="col-span-1">
            <button
              onClick={addCost}
              disabled={!newCost.description.trim() || newCost.amount <= 0}
              className="w-full px-2 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              data-testid="add-variable-cost-btn"
            >
              <Plus className="w-4 h-4 mx-auto" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Proposal History Panel Component
 */
const ProposalHistoryPanel = ({ tenantId, projectId, onLoad, onClose }) => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadProposals();
  }, [tenantId, projectId]);
  
  const loadProposals = async () => {
    try {
      setLoading(true);
      const response = await api.listProposals(tenantId, { project_id: projectId });
      setProposals(response.data.proposals || []);
    } catch (err) {
      console.error('Failed to load proposals:', err);
      toast.error('Failed to load proposal history');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLoad = async (proposal) => {
    if (proposal.id.length === 24) {
      // It's a saved proposal from the proposals collection
      try {
        const response = await api.getSavedProposal(proposal.id, tenantId);
        onLoad(response.data.proposal);
        toast.success(`Loaded "${proposal.name}" v${proposal.version}`);
      } catch (err) {
        toast.error('Failed to load proposal');
      }
    } else {
      // It's an embedded proposal in a project
      onLoad(proposal);
      toast.success('Loaded proposal');
    }
    onClose();
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'submitted': case 'review': return 'bg-yellow-100 text-yellow-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'converted': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };
  
  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-xl z-50 flex flex-col" data-testid="proposal-history-panel">
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-600" />
          Proposal History
        </h3>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
          </div>
        ) : proposals.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No saved proposals yet</p>
            <p className="text-xs text-gray-400 mt-1">Save your first proposal to see it here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {proposals.map((proposal) => (
              <div
                key={proposal.id}
                className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors cursor-pointer"
                onClick={() => handleLoad(proposal)}
                data-testid={`proposal-history-item-${proposal.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-medium text-sm text-gray-900">{proposal.name || proposal.client_name || 'Untitled'}</p>
                    <p className="text-xs text-gray-500">{proposal.project_name}</p>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(proposal.status)}`}>
                    {proposal.status || 'draft'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>v{proposal.version || 1}</span>
                  <span>{proposal.updated_at ? new Date(proposal.updated_at).toLocaleDateString() : 'N/A'}</span>
                </div>
                {proposal.total_cost && (
                  <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
                    Total: {proposal.currency || '$'}{proposal.total_cost?.toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="p-4 border-t bg-gray-50">
        <button
          onClick={loadProposals}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>
    </div>
  );
};

/**
 * Create Proposal Modal - Shown at the start to name and create the proposal project
 */
const CreateProposalModal = ({ onSubmit, onLoadFromHistory, loading }) => {
  const [proposalName, setProposalName] = useState('');
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="create-proposal-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Start New Proposal</h2>
          <p className="text-sm text-gray-500 mt-1">
            Give your proposal a name to begin the guided workflow
          </p>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proposal / Client Name *
            </label>
            <input
              type="text"
              value={proposalName}
              onChange={(e) => setProposalName(e.target.value)}
              placeholder="e.g., Acme Corp ERP Implementation"
              className="w-full px-4 py-3 text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              autoFocus
              data-testid="create-proposal-name-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && proposalName.trim()) {
                  onSubmit(proposalName);
                }
              }}
            />
            <p className="text-xs text-gray-400 mt-1">
              This creates a project to track your proposal progress
            </p>
          </div>
          
          <button
            onClick={() => onSubmit(proposalName)}
            disabled={!proposalName.trim() || loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            data-testid="create-proposal-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <ArrowRight className="w-5 h-5" />
            )}
            {loading ? 'Creating...' : 'Start Building Proposal'}
          </button>
          
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white text-gray-400">or</span>
            </div>
          </div>
          
          <button
            onClick={onLoadFromHistory}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="load-from-history-btn"
          >
            <BookOpen className="w-4 h-4" />
            Load from Proposal History
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Save Proposal Modal Component
 */
const SaveProposalModal = ({ tenantId, projectId, proposalData, estimationData, onClose, onSaved }) => {
  const [name, setName] = useState(proposalData?.clientName || '');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a proposal name');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.saveProposal(tenantId, projectId, {
        name: name.trim(),
        notes: notes.trim() || null,
        client_name: proposalData?.clientName,
        client_context: proposalData?.clientContext,
        problem_statement: proposalData?.problemStatement,
        scope_summary: proposalData?.scopeSummaryMarkdown,
        acceptance_summary: proposalData?.acceptanceSummaryMarkdown,
        kpi_summary: proposalData?.kpiSummaryMarkdown,
        risk_assumptions: proposalData?.riskAssumptionsMarkdown,
        approach: proposalData?.approachMarkdown,
        terms: proposalData?.termsMarkdown,
        estimation: estimationData || proposalData?.estimation,
        timeline: proposalData?.timeline,
        variable_costs: proposalData?.variableCosts || [],
        attachments: proposalData?.attachments || []
      });
      
      toast.success(`Saved as v${response.data.version}`);
      onSaved?.(response.data);
      onClose();
    } catch (err) {
      console.error('Failed to save proposal:', err);
      toast.error(err.response?.data?.detail || 'Failed to save proposal');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="save-proposal-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Save className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Save Proposal Version</h3>
            <p className="text-sm text-gray-500">Create a snapshot for history</p>
          </div>
        </div>
        
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Acme Corp - Phase 1"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
              data-testid="save-proposal-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes about this version..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            data-testid="confirm-save-proposal-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Version'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Proposal Step Component - Enhanced with History, Save, and Variable Costs
 */
const ProposalStep = ({ 
  projectId, 
  tenantId,
  data, 
  onChange, 
  onGenerate, 
  onExport, 
  onConvert, 
  loading, 
  exportLoading, 
  convertLoading,
  estimationData,
  currency = 'USD'
}) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [showHistory, setShowHistory] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [includeRates, setIncludeRates] = useState(data?.pricingEnabled ?? true);
  
  const tabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'scope', label: 'Scope' },
    { id: 'acceptance', label: 'Acceptance' },
    { id: 'kpis', label: 'KPIs' },
    { id: 'risks', label: 'Risks' },
    { id: 'approach', label: 'Approach' },
    { id: 'costs', label: 'Variable Costs' },
    { id: 'estimation', label: 'Estimation' },
    { id: 'terms', label: 'Terms' },
  ];
  
  const updateSection = (section, content) => {
    onChange({ ...data, [section]: content });
  };
  
  // Handle Include Rates toggle
  const handleRatesToggle = (checked) => {
    setIncludeRates(checked);
    onChange({ ...data, pricingEnabled: checked });
  };
  
  const handleLoadProposal = (loadedProposal) => {
    // Map saved proposal fields back to component state
    onChange({
      ...data,
      ...loadedProposal,
      scopeSummaryMarkdown: loadedProposal.scope_summary || loadedProposal.scopeSummaryMarkdown,
      acceptanceSummaryMarkdown: loadedProposal.acceptance_summary || loadedProposal.acceptanceSummaryMarkdown,
      kpiSummaryMarkdown: loadedProposal.kpi_summary || loadedProposal.kpiSummaryMarkdown,
      riskAssumptionsMarkdown: loadedProposal.risk_assumptions || loadedProposal.riskAssumptionsMarkdown,
      approachMarkdown: loadedProposal.approach || loadedProposal.approachMarkdown,
      termsMarkdown: loadedProposal.terms || loadedProposal.termsMarkdown,
      variableCosts: loadedProposal.variable_costs || loadedProposal.variableCosts || [],
      loadedFromHistory: true,
      loadedVersion: loadedProposal.version
    });
  };
  
  return (
    <div className="space-y-6">
      {/* History Panel */}
      {showHistory && (
        <ProposalHistoryPanel
          tenantId={tenantId}
          projectId={projectId}
          onLoad={handleLoadProposal}
          onClose={() => setShowHistory(false)}
        />
      )}
      
      {/* Save Modal */}
      {showSaveModal && (
        <SaveProposalModal
          tenantId={tenantId}
          projectId={projectId}
          proposalData={data}
          estimationData={estimationData}
          onClose={() => setShowSaveModal(false)}
          onSaved={() => {}}
        />
      )}
      
      {/* Generate Button */}
      {!data.scopeSummaryMarkdown && (
        <div className="space-y-6">
          {/* Templates Selector */}
          <ProposalTemplatesSelector
            tenantId={tenantId}
            projectId={projectId}
            compact={true}
            onTemplateApplied={(result) => {
              // Apply template sections to proposal data
              const sections = result.sections || [];
              const updates = {};
              
              sections.forEach(section => {
                if (section.id === 'exec-summary' || section.id === 'overview') {
                  updates.executiveSummary = section.content;
                } else if (section.id === 'scope') {
                  updates.scopeSummaryMarkdown = section.content;
                } else if (section.id === 'timeline') {
                  updates.timelineMarkdown = section.content;
                } else if (section.id === 'investment' || section.id === 'pricing') {
                  updates.pricingMarkdown = section.content;
                } else if (section.id === 'approach') {
                  updates.approachMarkdown = section.content;
                }
              });
              
              if (Object.keys(updates).length > 0) {
                onChange({ ...data, ...updates });
                toast.success(`Applied ${sections.length} sections from template`);
              }
            }}
          />
          
          {/* Or generate with AI */}
          <div className="text-center py-8">
            <div className="text-gray-400 text-sm mb-4">— or generate with AI —</div>
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Generate Proposal</h3>
            <p className="text-sm text-gray-500 mb-4">
              Create a professional client-ready proposal from all collected data
            </p>
            <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setShowHistory(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              data-testid="view-history-btn"
            >
              <BookOpen className="w-4 h-4" />
              View History
            </button>
            <button
              onClick={onGenerate}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
              data-testid="generate-proposal-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
              {loading ? 'Generating...' : 'Generate Proposal'}
            </button>
          </div>
        </div>
        </div>
      )}
      
      {/* Proposal Preview */}
      {data.scopeSummaryMarkdown && (
        <>
          {/* Loaded from History Banner */}
          {data.loadedFromHistory && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <BookOpen className="w-4 h-4" />
              <span>Loaded from history (v{data.loadedVersion})</span>
            </div>
          )}
          
          {/* Status & Actions */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                data.status === 'final' || data.status === 'approved' ? 'bg-green-100 text-green-700' :
                data.status === 'review' || data.status === 'submitted' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {data.status?.charAt(0).toUpperCase() + data.status?.slice(1) || 'Draft'}
              </span>
              {data.lastGeneratedAt && (
                <span className="text-xs text-gray-500">
                  Generated: {new Date(data.lastGeneratedAt).toLocaleString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHistory(true)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg"
                data-testid="history-btn"
              >
                <BookOpen className="w-3 h-3" />
                History
              </button>
              <button
                onClick={() => setShowSaveModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-green-600 hover:bg-green-50 rounded-lg"
                data-testid="save-version-btn"
              >
                <Save className="w-3 h-3" />
                Save Version
              </button>
              <button
                onClick={onGenerate}
                disabled={loading}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-purple-600 hover:bg-purple-50 rounded-lg"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                Regenerate
              </button>
              
              {/* Include Rates Toggle */}
              <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-600 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100" data-testid="include-rates-toggle">
                <input
                  type="checkbox"
                  checked={includeRates}
                  onChange={(e) => handleRatesToggle(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Include Rates</span>
              </label>
              
              <button
                onClick={() => onExport('docx')}
                disabled={exportLoading}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="export-docx-btn"
              >
                {exportLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                Word
              </button>
              <button
                onClick={() => onExport('pdf')}
                disabled={exportLoading}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                data-testid="export-pdf-btn"
                title="PDF export may not be available in all environments"
              >
                {exportLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                PDF
              </button>
            </div>
          </div>
          
          {/* Convert to Project CTA */}
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <FolderKanban className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Ready to Execute?</h4>
                  <p className="text-sm text-gray-600">
                    Convert this proposal into a live execution project in ScopeOps
                  </p>
                </div>
              </div>
              <button
                onClick={onConvert}
                disabled={convertLoading}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                data-testid="convert-to-project-btn"
              >
                {convertLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRightCircle className="w-4 h-4" />
                )}
                {convertLoading ? 'Creating...' : 'Convert to Project'}
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Tab Content */}
          <div className="p-4 bg-white border border-gray-200 rounded-lg min-h-[300px]">
            {activeTab === 'summary' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Executive Summary</h3>
                <textarea
                  value={data.scopeSummaryMarkdown || ''}
                  onChange={(e) => updateSection('scopeSummaryMarkdown', e.target.value)}
                  className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[200px]"
                />
              </div>
            )}
            {activeTab === 'scope' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Scope of Work</h3>
                <textarea
                  value={data.scopeSummaryMarkdown || ''}
                  onChange={(e) => updateSection('scopeSummaryMarkdown', e.target.value)}
                  className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[200px]"
                />
              </div>
            )}
            {activeTab === 'acceptance' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Acceptance Criteria</h3>
                <textarea
                  value={data.acceptanceSummaryMarkdown || ''}
                  onChange={(e) => updateSection('acceptanceSummaryMarkdown', e.target.value)}
                  className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[200px]"
                />
              </div>
            )}
            {activeTab === 'kpis' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Performance Indicators</h3>
                <textarea
                  value={data.kpiSummaryMarkdown || ''}
                  onChange={(e) => updateSection('kpiSummaryMarkdown', e.target.value)}
                  className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[200px]"
                />
              </div>
            )}
            {activeTab === 'risks' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Risks & Assumptions</h3>
                <textarea
                  value={data.riskAssumptionsMarkdown || ''}
                  onChange={(e) => updateSection('riskAssumptionsMarkdown', e.target.value)}
                  className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[200px]"
                />
              </div>
            )}
            {activeTab === 'approach' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Delivery Approach</h3>
                <textarea
                  value={data.approachMarkdown || ''}
                  onChange={(e) => updateSection('approachMarkdown', e.target.value)}
                  className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[200px]"
                />
              </div>
            )}
            {activeTab === 'costs' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Variable Costs</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Add non-resource costs like hardware, licenses, travel, and cloud services.
                </p>
                <VariableCostsEditor
                  costs={data.variableCosts || []}
                  onChange={(costs) => onChange({ ...data, variableCosts: costs })}
                  currency={currency}
                />
              </div>
            )}
            {activeTab === 'estimation' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Estimation & Pricing</h3>
                <p className="text-sm text-gray-600">
                  Estimation data is populated from the Estimation step.
                </p>
              </div>
            )}
            {activeTab === 'terms' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Terms & Conditions</h3>
                <textarea
                  value={data.termsMarkdown || ''}
                  onChange={(e) => updateSection('termsMarkdown', e.target.value)}
                  placeholder="Enter terms and conditions..."
                  className="w-full p-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[200px]"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Main Consulting Page
 */
const WIZARD_STORAGE_KEY = 'scopeops_proposal_wizard_state';

const ConsultingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { getFeature, scopingTier } = useProductMode();
  
  // TENANT ISOLATION: Use impersonated tenant if sysadmin is impersonating
  const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant?.id || user?.tenant_id;
  
  // Check if consulting mode is enabled
  const isConsultingEnabled = getFeature('consulting.mode', false);
  const isRateCardEnabled = getFeature('consulting.ratecard', false);
  
  // Load saved wizard state from localStorage
  const loadSavedState = () => {
    try {
      const saved = localStorage.getItem(WIZARD_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only load if saved within last 24 hours and for same tenant
        if (parsed.tenantId === tenantId && parsed.timestamp > Date.now() - 24 * 60 * 60 * 1000) {
          return parsed;
        }
      }
    } catch (err) {
      console.error('Failed to load wizard state:', err);
    }
    return null;
  };
  
  const savedState = loadSavedState();
  
  const [currentStep, setCurrentStep] = useState(savedState?.currentStep || 'intake');
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [projectId, setProjectId] = useState(searchParams.get('projectId') || savedState?.projectId || '');
  const [clientName, setClientName] = useState(savedState?.clientName || '');
  
  // Rate card state
  const [rateCard, setRateCard] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  
  // Step data - load from saved state or defaults
  const [intakeData, setIntakeData] = useState(savedState?.intakeData || {});
  const [discoveryData, setDiscoveryData] = useState(savedState?.discoveryData || {});
  const [scopeData, setScopeData] = useState(savedState?.scopeData || {});
  const [acceptanceData, setAcceptanceData] = useState(savedState?.acceptanceData || { acceptance: [], kpis: [] });
  const [planningData, setPlanningData] = useState(savedState?.planningData || {
    phases: [],
    milestones: [],
    roles: [],
    governance: { cadence: [], driftDetection: { enabled: false, thresholds: {} } },
    risks: [],
    dependencies: []
  });
  const [estimationData, setEstimationData] = useState(savedState?.estimationData || {});
  const [proposalData, setProposalData] = useState(savedState?.proposalData || {});
  
  // Convert to Project modal
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  
  // New: Create Proposal modal (shown when starting fresh without a projectId)
  const [showCreateProposalModal, setShowCreateProposalModal] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  
  // Check if we should show the create modal on mount (no existing project)
  useEffect(() => {
    if (!savedState?.projectId && !searchParams.get('projectId')) {
      setShowCreateProposalModal(true);
    }
  }, []);
  
  // Create a new proposal project
  const createProposalProject = async (proposalName) => {
    if (!proposalName.trim()) {
      toast.error('Please enter a proposal name');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.createProjectForScoping({
        tenant_id: tenantId,
        user_id: user?.id,
        name: proposalName.trim(),
        type: 'proposal',
        source: 'consulting_wizard',
        description: `Proposal: ${proposalName.trim()}`
      });
      
      const newProjectId = response.data.project_id || response.data.id;
      setProjectId(newProjectId);
      setClientName(proposalName.trim());
      setShowCreateProposalModal(false);
      toast.success(`Proposal "${proposalName}" created`);
    } catch (err) {
      console.error('Failed to create proposal project:', err);
      toast.error(err.response?.data?.detail || 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };
  
  // Save wizard state to localStorage whenever it changes
  useEffect(() => {
    const state = {
      tenantId,
      timestamp: Date.now(),
      currentStep,
      projectId,
      clientName,
      intakeData,
      discoveryData,
      scopeData,
      acceptanceData,
      planningData,
      estimationData,
      proposalData
    };
    try {
      localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
      console.error('Failed to save wizard state:', err);
    }
  }, [tenantId, currentStep, projectId, clientName, intakeData, discoveryData, scopeData, acceptanceData, planningData, estimationData, proposalData]);
  
  // Clear wizard state (for "Start New" action)
  const clearWizardState = () => {
    localStorage.removeItem(WIZARD_STORAGE_KEY);
    setCurrentStep('intake');
    setProjectId('');
    setClientName('');
    setIntakeData({});
    setDiscoveryData({});
    setScopeData({});
    setAcceptanceData({ acceptance: [], kpis: [] });
    setPlanningData({
      phases: [],
      milestones: [],
      roles: [],
      governance: { cadence: [], driftDetection: { enabled: false, thresholds: {} } },
      risks: [],
      dependencies: []
    });
    setEstimationData({});
    setProposalData({});
    toast.success('Wizard reset - starting fresh');
  };
  
  // Load rate card on mount
  useEffect(() => {
    if (tenantId && isRateCardEnabled) {
      loadRateCard();
    }
  }, [tenantId, isRateCardEnabled]);
  
  const loadRateCard = async () => {
    try {
      const response = await api.getRateCard(tenantId);
      setRateCard(response.data.rateCard?.filter(r => r.active) || []);
      setDefaultCurrency(response.data.defaultCurrency || 'USD');
    } catch (err) {
      console.log('Rate card not available:', err.response?.status);
    }
  };
  
  const openRateCard = () => {
    navigate('/consulting/rate-card');
  };
  
  // Run Discovery
  const runDiscovery = async () => {
    if (!discoveryData.clientContext || !discoveryData.problemStatement) {
      toast.error('Please enter client context and problem statement');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.consultingDiscover(tenantId, {
        clientContext: discoveryData.clientContext,
        problemStatement: discoveryData.problemStatement,
        industry: discoveryData.industry,
        constraints: discoveryData.constraints
      });
      
      setDiscoveryData({
        ...discoveryData,
        ...response.data
      });
      toast.success('Discovery completed');
    } catch (err) {
      console.error('Discovery failed:', err);
      toast.error(err.response?.data?.detail || 'Discovery failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Run Scoping
  const runScoping = async () => {
    if (!discoveryData.refinedSummary) {
      toast.error('Please complete discovery first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.consultingScope(tenantId, {
        refinedSummary: discoveryData.refinedSummary,
        discoveryNotes: discoveryData.clarifyingQuestions?.map((q, i) => ({
          id: `note_${i}`,
          text: q,
          source: 'ai'
        }))
      });
      
      setScopeData({
        ...scopeData,
        ...response.data
      });
      toast.success('Scope generated');
    } catch (err) {
      console.error('Scoping failed:', err);
      toast.error(err.response?.data?.detail || 'Scoping failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Run Estimation
  const runEstimation = async () => {
    if (!scopeData.scopeTree?.epics?.length) {
      toast.error('Please define scope first');
      return;
    }
    
    setLoading(true);
    try {
      // Build rate card for API if we have rates
      const partnerRateCard = rateCard.length > 0 
        ? rateCard.map(r => ({
            role: r.role,
            rate: r.baseRate * (1 + (r.markupPct || 0) / 100)
          }))
        : null;
      
      const response = await api.consultingEstimate(tenantId, {
        scopeTree: scopeData.scopeTree,
        partnerRateCard
      });
      
      // If we have a rate card, compute costs locally
      let estimation = response.data;
      
      if (rateCard.length > 0) {
        const rolesWithCosts = estimation.roles.map(role => {
          const rateCardRole = rateCard.find(r => r.role === role.role && r.active);
          if (rateCardRole) {
            const effectiveRate = rateCardRole.baseRate * (1 + (rateCardRole.markupPct || 0) / 100);
            return {
              ...role,
              rate: effectiveRate,
              costMin: role.hoursMin * effectiveRate,
              costMax: role.hoursMax * effectiveRate
            };
          }
          return role;
        });
        
        // Check if all roles have rates
        const allHaveRates = rolesWithCosts.every(r => r.rate != null);
        
        if (allHaveRates) {
          const totalCostMin = rolesWithCosts.reduce((sum, r) => sum + (r.costMin || 0), 0);
          const totalCostMax = rolesWithCosts.reduce((sum, r) => sum + (r.costMax || 0), 0);
          const totalHours = rolesWithCosts.reduce((sum, r) => sum + ((r.hoursMin + r.hoursMax) / 2), 0);
          const blendedRate = totalHours > 0 ? ((totalCostMin + totalCostMax) / 2) / totalHours : 0;
          
          estimation = {
            ...estimation,
            roles: rolesWithCosts,
            totalCostMin,
            totalCostMax,
            blendedRate,
            currency: defaultCurrency
          };
        } else {
          estimation = {
            ...estimation,
            roles: rolesWithCosts
          };
        }
      }
      
      setEstimationData(estimation);
      
      // Telemetry
      console.log('[Telemetry] consulting.estimate_generated', {
        totalHoursMin: estimation.totalHoursMin,
        totalHoursMax: estimation.totalHoursMax,
        totalCostMin: estimation.totalCostMin,
        totalCostMax: estimation.totalCostMax,
        currency: estimation.currency
      });
      
      toast.success('Estimation generated');
    } catch (err) {
      console.error('Estimation failed:', err);
      toast.error(err.response?.data?.detail || 'Estimation failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Generate Proposal
  const generateProposal = async () => {
    if (!projectId) {
      // If no project exists, show the create modal
      setShowCreateProposalModal(true);
      toast.info('Please name your proposal first to save your work');
      return;
    }
    
    setLoading(true);
    try {
      // Check if we have costs from rate card
      const hasCosts = estimationData.totalCostMin != null;
      
      const response = await api.generateProposal(tenantId, {
        projectId,
        clientName,
        pricingEnabled: hasCosts,
        currency: hasCosts ? defaultCurrency : undefined
      });
      
      // Merge estimation data into proposal
      const proposalWithEstimation = {
        ...response.data.proposal,
        estimation: {
          roles: estimationData.roles || [],
          totalHoursMin: estimationData.totalHoursMin,
          totalHoursMax: estimationData.totalHoursMax,
          totalCostMin: estimationData.totalCostMin,
          totalCostMax: estimationData.totalCostMax,
          blendedRate: estimationData.blendedRate,
          currency: estimationData.currency
        },
        timeline: estimationData.timeline || [],
        pricingEnabled: hasCosts
      };
      
      setProposalData(proposalWithEstimation);
      toast.success('Proposal generated');
    } catch (err) {
      console.error('Proposal generation failed:', err);
      toast.error(err.response?.data?.detail || 'Proposal generation failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Export Proposal
  const exportProposal = async (format) => {
    if (!projectId) {
      toast.error('No proposal to export');
      return;
    }
    
    setExportLoading(true);
    try {
      // Try to save proposal data first, but don't block export if it fails
      try {
        await api.updateProposalData(projectId, tenantId, {
          clientName: clientName || proposalData?.clientName,
          clientContext: discoveryData?.clientContext || proposalData?.clientContext,
          problemStatement: discoveryData?.problemStatement || proposalData?.problemStatement,
          pricingEnabled: (estimationData?.totalCostMin != null) || proposalData?.pricingEnabled,
          scopeSummaryMarkdown: proposalData?.scopeSummaryMarkdown,
          acceptanceSummaryMarkdown: proposalData?.acceptanceSummaryMarkdown,
          kpiSummaryMarkdown: proposalData?.kpiSummaryMarkdown,
          riskAssumptionsMarkdown: proposalData?.riskAssumptionsMarkdown,
          approachMarkdown: proposalData?.approachMarkdown,
          termsMarkdown: proposalData?.termsMarkdown,
          estimation: proposalData?.estimation || (estimationData?.roles ? {
            roles: estimationData.roles || [],
            totalHoursMin: estimationData.totalHoursMin,
            totalHoursMax: estimationData.totalHoursMax,
            totalCostMin: estimationData.totalCostMin,
            totalCostMax: estimationData.totalCostMax,
            blendedRate: estimationData.blendedRate,
            currency: estimationData.currency || defaultCurrency
          } : undefined),
          timeline: proposalData?.timeline || estimationData?.timeline || [],
          variable_costs: proposalData?.variableCosts || proposalData?.variable_costs || [],
          planning: planningData || proposalData?.planning,
          scopeTree: scopeData?.scopeTree || proposalData?.scopeTree
        });
      } catch (syncErr) {
        console.warn('Proposal sync before export failed (continuing with export):', syncErr);
      }
      
      // Now export
      const response = await api.exportProposal(projectId, tenantId, format);
      
      // Check if response is an error (JSON instead of blob)
      if (response.data instanceof Blob) {
        // Check if it's actually an error response
        if (response.data.type === 'application/json') {
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.detail || 'Export failed');
        }
      }
      
      // Download the file
      const blob = response.data instanceof Blob 
        ? response.data 
        : new Blob([response.data], { 
            type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
          });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposal_${clientName || proposalData?.clientName || 'export'}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} exported`);
    } catch (err) {
      console.error('Export failed:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Export failed';
      
      // If PDF failed, suggest Word as alternative
      if (format === 'pdf' && errorMessage.includes('unavailable')) {
        toast.error('PDF export unavailable. Please use Word export instead.', {
          duration: 5000
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setExportLoading(false);
    }
  };
  
  // Navigation
  const goToStep = (stepId) => {
    const stepIdx = STEPS.findIndex(s => s.id === stepId);
    const currentIdx = STEPS.findIndex(s => s.id === currentStep);
    
    // Only allow going back or to completed steps
    if (stepIdx <= currentIdx) {
      setCurrentStep(stepId);
    }
  };
  
  const nextStep = () => {
    const currentIdx = STEPS.findIndex(s => s.id === currentStep);
    if (currentIdx < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIdx + 1].id);
    }
  };
  
  const prevStep = () => {
    const currentIdx = STEPS.findIndex(s => s.id === currentStep);
    if (currentIdx > 0) {
      setCurrentStep(STEPS[currentIdx - 1].id);
    }
  };
  
  // Check step completion
  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 'intake': return !!intakeData.extractedRequirements || !!intakeData.skipped;
      case 'discovery': return !!discoveryData.refinedSummary;
      case 'scope': return scopeData.scopeTree?.epics?.length > 0;
      case 'acceptance': return true; // Optional
      case 'planning': return planningData.phases?.length > 0;
      case 'estimation': return estimationData.roles?.length > 0;
      case 'proposal': return !!proposalData.scopeSummaryMarkdown;
      default: return false;
    }
  };
  
  // Convert Proposal to Project
  const convertToProject = async () => {
    setConvertLoading(true);
    try {
      // First, save the current proposal if we have a projectId
      let proposalIdToConvert = proposalData?.id;
      
      if (projectId && !proposalIdToConvert) {
        // Save the proposal first to get an ID
        const saveResponse = await api.saveProposal(tenantId, projectId, {
          name: clientName || 'Proposal',
          client_name: clientName,
          problem_statement: discoveryData?.problemStatement,
          scope_summary: proposalData?.scopeSummaryMarkdown,
          acceptance_summary: proposalData?.acceptanceSummaryMarkdown,
          kpi_summary: proposalData?.kpiSummaryMarkdown,
          risk_assumptions: proposalData?.riskAssumptionsMarkdown,
          approach: proposalData?.approachMarkdown,
          terms: proposalData?.termsMarkdown,
          estimation: estimationData,
          timeline: proposalData?.timeline || estimationData?.timeline,
          variable_costs: proposalData?.variableCosts || [],
        });
        proposalIdToConvert = saveResponse.data.proposal_id;
      }
      
      // Call the backend conversion API
      const response = await api.convertProposalToProject(
        tenantId,
        user?.id || 'system',
        {
          proposal_id: proposalIdToConvert,
          project_name: `${clientName || 'Client'} - ${discoveryData?.problemStatement?.slice(0, 50) || 'Project'}`,
          include_requirements: true
        }
      );
      
      if (response.data?.success && response.data?.project_id) {
        toast.success(`Project created with ${response.data.requirements_created || 0} requirements!`);
        setShowConvertModal(false);
        // Navigate to the new project
        navigate(`/scope/workspace?project=${response.data.project_id}`);
      } else {
        throw new Error('Conversion failed - no project ID returned');
      }
    } catch (err) {
      console.error('Convert to project failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setConvertLoading(false);
    }
  };
  
  // Gate for non-enabled tenants
  if (!isConsultingEnabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <TierGate
          requiredTier="pro"
          feature="Consulting Mode"
          description="Create professional client proposals with AI-powered discovery, scoping, and estimation."
        />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Proposal Builder</h1>
              <p className="text-xs text-gray-500">Agent-led Consulting Mode</p>
            </div>
            {/* Saved state indicator */}
            {savedState && (
              <span className="ml-3 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                Progress restored
              </span>
            )}
          </div>
          
          {/* Client Name & Actions */}
          <div className="flex items-center gap-3">
            {/* Project ID indicator */}
            {projectId && (
              <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                Saved
              </span>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Proposal Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter proposal name..."
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 w-48"
                disabled={!projectId}
              />
            </div>
            <button
              onClick={() => setShowHistoryPanel(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              title="View proposal history"
              data-testid="global-history-btn"
            >
              <BookOpen className="w-4 h-4" />
              History
            </button>
            <button
              onClick={() => {
                clearWizardState();
                setShowCreateProposalModal(true);
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Clear all data and start fresh"
              data-testid="start-new-proposal-btn"
            >
              <RotateCcw className="w-4 h-4" />
              New
            </button>
          </div>
        </div>
      </div>
      
      {/* Step Progress */}
      <StepProgress 
        currentStep={currentStep} 
        steps={STEPS}
        onStepClick={goToStep}
      />
      
      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-6">
          {/* Proactive Suggestions */}
          <ProactiveSuggestionsBanner
            context={`consulting_${currentStep}`}
            entityType="consulting"
            className="mb-6"
          />
          
          {/* Step Content */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {currentStep === 'intake' && (
              <IntakeStep
                data={intakeData}
                onChange={setIntakeData}
                tenantId={tenantId}
                loading={loading}
                onFileProcessed={(extracted) => {
                  // Auto-populate discovery context from extracted data
                  if (extracted && !extracted.skipped && extracted.items?.length > 0) {
                    const themes = extracted.items.filter(i => i.type === 'theme');
                    const requirements = extracted.items.filter(i => i.type !== 'theme');
                    
                    // Build context summary
                    const contextSummary = themes.map(t => t.title || t.name).join(', ');
                    const reqSummary = requirements.slice(0, 5).map(r => `- ${r.title || r.name}`).join('\n');
                    
                    setDiscoveryData(prev => ({
                      ...prev,
                      clientContext: `Extracted from: ${extracted.file_name || 'uploaded document'}\n\nKey Themes: ${contextSummary}\n\nSample Requirements:\n${reqSummary}`,
                      problemStatement: `Based on ${extracted.items.length} extracted requirements across ${themes.length} theme(s).`
                    }));
                  }
                }}
              />
            )}
            
            {currentStep === 'discovery' && (
              <DiscoveryStep
                data={discoveryData}
                onChange={setDiscoveryData}
                onGenerate={runDiscovery}
                loading={loading}
              />
            )}
            
            {currentStep === 'scope' && (
              <ScopeStep
                data={scopeData}
                onChange={setScopeData}
                onGenerate={runScoping}
                loading={loading}
                discoveryData={discoveryData}
              />
            )}
            
            {currentStep === 'acceptance' && (
              <AcceptanceKPICard
                projectId={projectId}
                demandSummary={discoveryData.refinedSummary}
                initialAcceptance={acceptanceData.acceptance}
                initialKPIs={acceptanceData.kpis}
                onSave={({ acceptance, kpis }) => setAcceptanceData({ acceptance, kpis })}
                variant="step"
              />
            )}
            
            {currentStep === 'planning' && (
              <PlanningStep
                data={planningData}
                onChange={setPlanningData}
                scopeData={scopeData}
                discoveryData={discoveryData}
              />
            )}
            
            {currentStep === 'estimation' && (
              <EstimationStep
                data={estimationData}
                onChange={setEstimationData}
                onGenerate={runEstimation}
                loading={loading}
                scopeData={scopeData}
                planData={planningData}
                rateCard={rateCard}
                currency={defaultCurrency}
                onOpenRateCard={openRateCard}
              />
            )}
            
            {currentStep === 'proposal' && (
              <ProposalStep
                projectId={projectId}
                tenantId={tenantId}
                data={proposalData}
                onChange={setProposalData}
                onGenerate={generateProposal}
                onExport={exportProposal}
                onConvert={() => setShowConvertModal(true)}
                loading={loading}
                exportLoading={exportLoading}
                convertLoading={convertLoading}
                estimationData={estimationData}
                currency={defaultCurrency}
              />
            )}
          </div>
          
          {/* Convert to Project Confirmation Modal */}
          {showConvertModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <FolderKanban className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Convert to Project</h3>
                    <p className="text-sm text-gray-500">Create an execution project from this proposal</p>
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <p className="text-sm text-gray-600">
                    This will create a new project in ScopeOps with:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 pl-4">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Scope tree ({scopeData.scopeTree?.epics?.length || 0} epics, {scopeData.scopeTree?.epics?.reduce((sum, e) => sum + (e.features?.length || 0), 0) || 0} features)
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Acceptance criteria & KPIs
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Delivery plan ({planningData.phases?.length || 0} phases, {planningData.milestones?.length || 0} milestones)
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Risks & Dependencies
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Governance cadence
                    </li>
                  </ul>
                </div>
                
                {/* Agentic PM Option */}
                <div className="p-3 mb-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      defaultChecked={true}
                      className="w-5 h-5 mt-0.5 rounded text-purple-600 focus:ring-purple-500"
                      data-testid="enable-aipm-checkbox"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        Enable Agentic PM
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded">AI</span>
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        AI-powered monitoring, insights, and suggestions for this project
                      </p>
                    </div>
                  </label>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowConvertModal(false)}
                    className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={convertToProject}
                    disabled={convertLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    data-testid="confirm-convert-btn"
                  >
                    {convertLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ArrowRightCircle className="w-4 h-4" />
                    )}
                    {convertLoading ? 'Creating...' : 'Create Project'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={prevStep}
              disabled={currentStep === 'intake'}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            
            <button
              onClick={nextStep}
              disabled={!isStepComplete(currentStep) || currentStep === 'proposal'}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {currentStep === 'proposal' ? 'Complete' : 'Continue'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Create Proposal Modal */}
      {showCreateProposalModal && (
        <CreateProposalModal
          onSubmit={createProposalProject}
          onLoadFromHistory={() => {
            setShowCreateProposalModal(false);
            setShowHistoryPanel(true);
          }}
          loading={loading}
        />
      )}
      
      {/* Global History Panel */}
      {showHistoryPanel && (
        <ProposalHistoryPanel
          tenantId={tenantId}
          projectId={projectId}
          onLoad={(loadedProposal) => {
            // Load the proposal and its project
            if (loadedProposal.project_id) {
              setProjectId(loadedProposal.project_id);
            }
            setClientName(loadedProposal.name || loadedProposal.client_name || '');
            
            // Set all proposal data including nested fields
            const loadedProposalData = {
              ...proposalData,
              ...loadedProposal,
              clientName: loadedProposal.name || loadedProposal.client_name || loadedProposal.clientName,
              clientContext: loadedProposal.client_context || loadedProposal.clientContext,
              problemStatement: loadedProposal.problem_statement || loadedProposal.problemStatement,
              scopeSummaryMarkdown: loadedProposal.scope_summary || loadedProposal.scopeSummaryMarkdown,
              acceptanceSummaryMarkdown: loadedProposal.acceptance_summary || loadedProposal.acceptanceSummaryMarkdown,
              kpiSummaryMarkdown: loadedProposal.kpi_summary || loadedProposal.kpiSummaryMarkdown,
              riskAssumptionsMarkdown: loadedProposal.risk_assumptions || loadedProposal.riskAssumptionsMarkdown,
              approachMarkdown: loadedProposal.approach || loadedProposal.approachMarkdown,
              termsMarkdown: loadedProposal.terms || loadedProposal.termsMarkdown,
              variableCosts: loadedProposal.variable_costs || loadedProposal.variableCosts || [],
              timeline: loadedProposal.timeline || [],
              planning: loadedProposal.planning,
              scopeTree: loadedProposal.scopeTree || loadedProposal.scope_tree,
              pricingEnabled: loadedProposal.pricingEnabled || (loadedProposal.estimation?.totalCostMin != null),
              loadedFromHistory: true,
              loadedVersion: loadedProposal.version
            };
            setProposalData(loadedProposalData);
            
            // Also update the other state variables for consistency
            if (loadedProposal.estimation) {
              setEstimationData(loadedProposal.estimation);
            }
            if (loadedProposal.planning) {
              setPlanningData(loadedProposal.planning);
            }
            if (loadedProposal.scopeTree || loadedProposal.scope_tree) {
              setScopeData(prev => ({ ...prev, scopeTree: loadedProposal.scopeTree || loadedProposal.scope_tree }));
            }
            if (loadedProposal.client_context || loadedProposal.clientContext || loadedProposal.problem_statement || loadedProposal.problemStatement) {
              setDiscoveryData(prev => ({
                ...prev,
                clientContext: loadedProposal.client_context || loadedProposal.clientContext || prev?.clientContext,
                problemStatement: loadedProposal.problem_statement || loadedProposal.problemStatement || prev?.problemStatement
              }));
            }
            
            // Go to proposal step since they loaded a proposal
            setCurrentStep('proposal');
            setShowHistoryPanel(false);
            toast.success(`Loaded "${loadedProposal.name}" v${loadedProposal.version}`);
          }}
          onClose={() => setShowHistoryPanel(false)}
        />
      )}
    </div>
  );
};

export default ConsultingPage;
